/**
 * Session-fetch tracking — does a query hold data the server returned in THIS
 * session, or only a persisted copy restored from a previous one?
 *
 * Persisted queries are restored by `hydrate` (a `setState` action) and
 * realtime patches go through `setQueryData` (a manual `success`), so only a
 * network fetch dispatches a non-manual `success`. Tracking is per `Query`
 * instance: a removed and rebuilt query starts unfetched.
 *
 * Callers gate irreversible actions on it (e.g. a prize claim must not burn
 * an amount read from a restored pre-session snapshot).
 */
import { useQueryClient, } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
const fetchedThisSession = new WeakSet();
const listeners = new Set();
/** Fed every QueryCache event by the SDKProvider's unified cache listener. */
export function recordSessionFetchEvent(event) {
    if (event.type === 'removed') {
        if (fetchedThisSession.has(event.query))
            notify();
        return;
    }
    if (event.type !== 'updated')
        return;
    if (event.action.type !== 'success' || event.action.manual === true)
        return;
    if (fetchedThisSession.has(event.query))
        return;
    fetchedThisSession.add(event.query);
    notify();
}
/**
 * True once the query at `queryKey` has been fetched from the server in this
 * session; false while it is absent or holds only restored/patched data.
 */
export function useIsFetchedThisSession(queryKey) {
    const queryClient = useQueryClient();
    return useSyncExternalStore(subscribe, () => {
        const query = queryClient.getQueryCache().find({ queryKey, exact: true });
        return query !== undefined && fetchedThisSession.has(query);
    }, () => false);
}
function subscribe(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
function notify() {
    for (const listener of listeners)
        listener();
}
//# sourceMappingURL=session-fetch.js.map